// src/pages/EditIncentivePlan.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import Breadcrumb from "../components/Breadcrumb";
import CustomSelect from "../components/common/CustomSelect";
import { CancelButtonForm, UpdateButtonForm } from "../components/ui/Button";
import { handleCustomResponse } from "../utils/response";
import { staffApi } from "../Apis/Api";
import { apiCall } from "../Apis/apiCall";

const validationSchema = Yup.object().shape({
  plan_name: Yup.string().required("Plan name is required"),
  plan_type: Yup.string().required("Plan type is required"),
  period_type: Yup.object()
    .shape({
      value: Yup.string().required("Period type is required"),
      label: Yup.string(),
    })
    .required("Period type is required"),
  value_type: Yup.object()
    .shape({
      value: Yup.string().required("Value type is required"),
      label: Yup.string(),
    })
    .required("Value type is required"),
  incentive_value: Yup.number()
    .typeError("Incentive value must be a number")
    .required("Incentive value is required")
    .min(0, "Incentive value must be greater than or equal to 0")
    .test(
      "maxDigits",
      "Incentive value cannot have more than 2 decimal places",
      (value) => !value || /^\d+(\.\d{1,2})?$/.test(value.toString())
    ),
  currency_code: Yup.string().when("value_type", {
    is: (value_type) => value_type?.value === "currency",
    then: (schema) => schema.required("Currency code is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  is_active: Yup.boolean().required("Status is required"),
});

const EditIncentivePlan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const planData = location.state?.staffData || {};

  // Get user currency from localStorage
  useEffect(() => {
    const getUserCurrency = () => {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          return user.currency_code || "";
        }
      } catch (error) {
        console.error("Error getting user currency from localStorage:", error);
      }
      return "";
    };

    setDefaultCurrency(getUserCurrency());
  }, []);

  // Period type options
  const periodTypeOptions = [
    { label: "Monthly", value: "monthly" },
    { label: "Daily", value: "daily" },
    { label: "Weekly", value: "weekly" },
  ];

  // Value type options
  const valueTypeOptions = [
    { label: "Currency", value: "currency" },
    { label: "Points", value: "points" },
  ];

  // Status options
 const statusOptions = [
  { label: "Active", value: "true" },
  { label: "Inactive", value: "false" },
];

  // Fetch currency codes
  useEffect(() => {
    const fetchCurrencyCodes = async () => {
      try {
        const res = await apiCall("get", "/get-currency-codes?want_pagination=0");
        if (res.data.success) {
          const uniqueCurrencies = Array.from(
            new Map(res.data.data.map((currency) => [currency.currency_code, currency])).values()
          );
          const options = uniqueCurrencies.map((currency) => ({
            label: `${currency.currency_code} (${currency.currency_symbol})`,
            value: currency.currency_code,
          }));
          setCurrencyOptions(options);
        } else {
          console.error("Failed to fetch currency codes:", res.message);
          handleCustomResponse("error", res.message || "Failed to fetch currency codes!");
        }
      } catch (err) {
        console.error("Error fetching currency codes:", err);
        handleCustomResponse("error", "Error fetching currency codes!");
      }
    };

    fetchCurrencyCodes();
  }, []);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      plan_name: "",
      plan_type: "location_based",
      period_type: null,
      value_type: null,
      incentive_value: "",
      currency_code: defaultCurrency,
      is_active: true,
    },
  });

  // Set default currency when it's available
  useEffect(() => {
    if (defaultCurrency) {
      setValue("currency_code", defaultCurrency);
    }
  }, [defaultCurrency, setValue]);

  // Watch value_type to conditionally show/hide currency_code field
  const selectedValueType = watch("value_type");

  // Pre-populate form with plan data
  useEffect(() => {
    if (planData && Object.keys(planData).length > 0) {
      console.log("Populating form with data:", planData);

      // Set basic string/number fields
      setValue("plan_name", planData.plan_name || "");
      setValue("incentive_value", planData.fixed_value || "");
      // Currency code ko planData se nahi, balki defaultCurrency se set karenge
      setValue("currency_code", defaultCurrency || "");
      setValue("is_active", planData.is_active === "Active" ? true : false);

      // Set period_type dropdown
      const periodOption = periodTypeOptions.find(
        (option) => option.value === planData.period_type?.toLowerCase()
      );
      setValue("period_type", periodOption || null);

      // Set value_type dropdown
      const valueOption = valueTypeOptions.find(
        (option) => option.value === planData.value_type?.toLowerCase()
      );
      setValue("value_type", valueOption || null);

      console.log("Form populated successfully");
    }
  }, [planData, setValue, defaultCurrency]);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const payload = {
        plan_name: data.plan_name,
        plan_type: data.plan_type,
        period_type: data.period_type.value.toUpperCase(),
        value_type: data.value_type.value.toUpperCase(),
        fixed_value: parseFloat(data.incentive_value),
        currency_code:
          data.value_type.value === "currency"
            ? data.currency_code.toUpperCase()
            : null,
        is_active: data.is_active,
      };

      console.log("Form Data:", payload);

      const response = await staffApi.updateIncentivePlan(planData.id, payload);

      console.log("API Response:", response);

      if (response.success) {
        handleCustomResponse(
          "success",
          response.message || "Plan updated successfully!"
        );
        setTimeout(() => {
          navigate(-1);
        }, 1000);
      } else {
        handleCustomResponse(
          "error",
          response.message || "Failed to update plan!"
        );
      }
    } catch (err) {
      console.error("Error updating incentive plan:", err);
      let errorMessage = "Failed to update plan!";
      if (typeof err === "string") {
        errorMessage = err;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.error) {
        errorMessage = err.error;
      }
      handleCustomResponse("error", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Breadcrumb title="Edit Incentive Plan" />

      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <div className="row g-20">
              <div className="col-md-6 mb-20">
                <label htmlFor="plan_name" className="form-label">
                  Plan Name *
                </label>
                <input
                  id="plan_name"
                  type="text"
                  {...register("plan_name")}
                  className="form-control radius-8"
                  placeholder="Enter Plan Name"
                />
                {errors.plan_name && (
                  <span className="text-danger-600">
                    {errors.plan_name.message}
                  </span>
                )}
              </div>

              <div className="col-md-6 mb-20">
                <label className="form-label">Period Type *</label>
                <Controller
                  name="period_type"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      name="period_type"
                      value={field.value}
                      onChange={field.onChange}
                      options={periodTypeOptions}
                      placeholder="Select Period Type"
                      isClearable
                      noOptionsMessage={() => (
                        <div>
                          <p>No period types available</p>
                        </div>
                      )}
                    />
                  )}
                />
                {errors.period_type && (
                  <span className="text-danger-600">
                    {errors.period_type.message}
                  </span>
                )}
              </div>

              <div className="col-md-6 mb-20">
                <label className="form-label">Value Type *</label>
                <Controller
                  name="value_type"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      name="value_type"
                      value={field.value}
                      onChange={field.onChange}
                      options={valueTypeOptions}
                      placeholder="Select Value Type"
                      isClearable
                      noOptionsMessage={() => (
                        <div>
                          <p>No value types available</p>
                        </div>
                      )}
                    />
                  )}
                />
                {errors.value_type && (
                  <span className="text-danger-600">
                    {errors.value_type.message}
                  </span>
                )}
              </div>

              <div className="col-md-6 mb-20">
                <label htmlFor="incentive_value" className="form-label">
                  Incentive Value *
                </label>
                <input
                  id="incentive_value"
                  type="number"
                  step="0.01"
                  min="0"
                  {...register("incentive_value")}
                  className="form-control radius-8"
                  placeholder="Enter Incentive Value"
                />
                {errors.incentive_value && (
                  <span className="text-danger-600">
                    {errors.incentive_value.message}
                  </span>
                )}
              </div>

              {selectedValueType?.value === "currency" && (
                <div className="col-md-6 mb-20">
                  <label htmlFor="currency_code" className="form-label">
                    Currency Code *
                  </label>
                  <input
                    id="currency_code"
                    type="text"
                    {...register("currency_code")}
                    className="form-control radius-8"
                    placeholder="Currency Code"
                    value={defaultCurrency}
                    readOnly
                    disabled
                  />
                  <small className="">
                    Using your default currency: {defaultCurrency}
                  </small>
                  {errors.currency_code && (
                    <span className="text-danger-600">{errors.currency_code.message}</span>
                  )}
                </div>
              )}

              <div className="col-md-6 mb-20">
                <label className="form-label">Status *</label>
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      name="is_active"
                     value={statusOptions.find(
          (option) => option.value === field.value.toString()
        )}
        onChange={(option) =>
          field.onChange(option ? option.value === "true" : null)
        }
                      options={statusOptions}
                      placeholder="Select Status"
                      isClearable
                      noOptionsMessage={() => (
                        <div>
                          <p>No status options available</p>
                        </div>
                      )}
                    />
                  )}
                />
                {errors.is_active && (
                  <span className="text-danger-600">
                    {errors.is_active.message}
                  </span>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-center gap-3">
              <CancelButtonForm />
              <UpdateButtonForm disabled={isLoading} />
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditIncentivePlan;